import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Icon } from '@mui/material';
import styles from './TemplateCsvModal.module.scss';
import { Text } from '../typography';
import Button from '../Button';
import { loader } from 'graphql.macro';
import { ProgressBar } from 'react-bootstrap';
import graphic from '../../assets/cloud_upload.svg';
import { showToast } from '../../graphql/cache/modal';
import { toastLength } from '../../constants/misc';
import { useMutation } from '@apollo/client';
import { sleep } from '../../utilities/time';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { csvInputTypes, templateFilePaths } from '../../constants/strings';
import { inputTypes } from '../../utilities/inspection';
import simplifiedExcelTemplate from '../../assets/template/OPASTemplateCreator.xlsx';

const categoryMutation = loader('./TemplateCsvModal.category.graphql');
const questionMutation = loader('./TemplateCsvModal.question.graphql');

const REQUEST_THROTTLE = 100;

export default function TemplateCsvModal({
  show = false,
  onHide,
  template,
  refetch,
}) {
  const [addCategory] = useMutation(categoryMutation);
  const [addQuestion] = useMutation(questionMutation);
  const [completed, setCompleted] = useState(0);
  const [loading, setLoading] = useState(false);
  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const [progress, setProgress] = useState(0);
  const { t } = useTranslation();
  const instructionsLink = (
    <a
      className={styles.resetAnchor}
      href={templateFilePaths.ImportInstructions}
      target="blank"
      download
    >
      <Text noMargin color="accentPrimary" weight="bold" textAlign="center">
        {t(keys.templates.CSV_INSTRUCTIONS)}
      </Text>
    </a>
  );

  const clearModal = () => {
    setProgress(0);
    setCompleted(0);
    setNumberOfQuestions(0);
    setLoading(false);
  };

  useEffect(() => {
    if (progress === 100) {
      setTimeout(() => {
        setLoading(false);
        refetch();
        onHide();
        clearModal();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress]);

  useEffect(() => {
    if (loading && numberOfQuestions && completed) {
      setProgress(Math.round((completed / numberOfQuestions) * 100));
    }

    if (numberOfQuestions === completed && loading) {
    }
  }, [loading, numberOfQuestions, completed]);

  const formatAdditional = ({ additional, max, min, inputType }) => {
    if (!additional || !inputType) return null;
    switch (inputType) {
      case inputTypes.test:
        return (
          additional
            ?.split(',')
            .map((option) => option.trim())
            .join('|') ?? null
        );
      case inputTypes.number:
        return !!min || !!max ? `${min ?? ''}|${max ?? ''}` : null;
      default:
        return (
          additional
            ?.split(',')
            .map((option) => option.trim())
            .join('|') ?? ''
        );
    }
  };
  const handleExcelUploadSimplified = async (e) => {
    setCompleted(0);
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      const xlsx = await import('xlsx');
      const data = new Uint8Array(e.target.result);
      const workbook = xlsx.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = xlsx.utils.sheet_to_json(worksheet, { header: 1 });
      const nonEmptyRows = rawRows.filter((row) =>
        row.some((cell) => cell !== undefined && cell !== null && cell !== ''),
      );

      if (nonEmptyRows.length <= 1) {
        showToast({
          title: 'Error',
          variant: 'danger',
          time: toastLength.lg,
          message: 'No data found in the Excel file.',
        });
        onHide();
        return;
      }
      const headers = nonEmptyRows[0];
      const dataRows = nonEmptyRows;

      const rows = dataRows.slice(1).map((row) => {
        const obj = {};
        headers?.forEach((header, index) => {
          obj[header?.toLowerCase() || header] = row[index];
        });
        return obj;
      });
      let categories = {};
      const questionRows = [];

      if (template?.templateType === 'OBSERVATION') {
        categories = template.categories;
        setNumberOfQuestions(rows.length);
        setLoading(true);
        rows.forEach((row) => questionRows.push(row));
      }

      if (template?.templateType !== 'OBSERVATION') {
        rows.forEach((row) => {
          if (!!row.category) {
            categories[row.category] = {
              title: row.category,
            };
          } else {
            categories['NONE'] = { title: 'NONE' };
          }
          questionRows.push(row);
        });
        setNumberOfQuestions(rows.length + Object.keys(categories).length);
        setLoading(true);

        for (let i = 0; i < Object.keys(categories).length; i++) {
          await sleep(REQUEST_THROTTLE);
          const key = Object.keys(categories)[i];
          const variables = {
            templateId: template.id,
            title: `${categories[key].title}`,
            isBundle: false,
          };
          const {
            data: {
              addInspectionTemplateCategory: { id: categoryId },
            },
          } = await addCategory({ variables });

          categories[key].id = categoryId;
          const targetCompleted = i + 1;
          setCompleted(targetCompleted);
        }
      }

      for (let i = 0; i < questionRows.length; i++) {
        await sleep(REQUEST_THROTTLE);
        const targetCompleted =
          template?.templateType !== 'OBSERVATION'
            ? Object.keys(categories).length + 1 + i
            : questionRows?.length + i;

        const {
          title,
          description,
          category,
          input_type: inputType,
          required,
          additional,
        } = questionRows[i];
        const inputTypeTrimmed = inputType ? inputType.trim() : '';
        const additionalData = formatAdditional({
          additional,
          inputType: csvInputTypes.includes(inputTypeTrimmed)
            ? inputTypeTrimmed
            : 'INVALID',
        });

        const categoryId =
          template.templateType === 'OBSERVATION'
            ? categories[0].id
            : categories[category ?? 'NONE']?.id;

        const variables = {
          categoryId,
          templateId: template.id,
          title: `${title}` || 'No Title',
          description: description || '',
          inputType: inputTypeTrimmed,
          required: required?.toUpperCase() === 'YES' || false,
          additionalData,
        };
        await addQuestion({ variables });
        setCompleted(targetCompleted);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return show ? (
    <div className={show ? styles.show : styles.hide}>
      <div className={styles.background}>
        <div
          className={classNames(
            styles.container,
            show ? styles.showContainer : styles.hideContainer,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.header}>
            <Text
              className={styles.header}
              size="lg"
              weight="semiBold"
              noMargin
            >
              {t(keys.action.IMPORT, { variable: t(keys.common.FILE) })}
            </Text>
            <Icon
              className={styles.closeIcon}
              onClick={() => {
                onHide();
                clearModal();
              }}
            >
              close
            </Icon>
          </div>
          {loading ? (
            <div className={styles.spinnerContainer}>
              <ProgressBar animated now={progress} />
            </div>
          ) : (
            <>
              <img src={graphic} className={styles.graphic} alt="csv" />
              <br />
              <Text textAlign="center" size="lg" weight="semiBold" noMargin>
                {t(keys.action.ADD, { variable: t(keys.templates.CSV_FILE) })}
              </Text>
              <div className={styles.uploadButton}>
                <label>
                  <Text
                    noMargin
                    weight="semiBold"
                    className={styles.fileUploadButton}
                    textAlign="center"
                  >
                    <input
                      className={styles.hideDefault}
                      type={'file'}
                      id={'csvFileInput'}
                      accept=".csv,.xlsx"
                      onChange={(e) => {
                        handleExcelUploadSimplified(e);
                      }}
                      onClick={() => {}}
                    />
                    {t(keys.action.SELECT_VARIABLE, {
                      variable: t(keys.common.FILE),
                    })}
                  </Text>
                </label>
              </div>
              <Text
                textAlign="center"
                size="md"
                weight="semiBold"
                noMargin
                color="secondary"
              >
                {t(keys.templates.CSV_UPLOAD_MODAL_TEXT)}
              </Text>
              {instructionsLink}
              <Text
                textAlign="center"
                size="md"
                weight="semiBold"
                noMargin
                color="secondary"
              >
                or
              </Text>
              <a
                className={styles.resetAnchor}
                href={simplifiedExcelTemplate}
                target="blank"
                download={true}
              >
                <Button
                  className={styles.downloadButton}
                  size="md"
                  value={t(keys.templates.DOWNLOAD_EXCEL_TEMPLATE)}
                />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;
}
